import React, { useState } from 'react'
import { api } from '../../utils/api'

// Same limits as the worker route (workers/src/services/photographyCancel.js).
export const REASON_MIN = 3
export const REASON_MAX = 200

// Selected quick pick: brand green fill, white text. Unselected stay outlined.
const SELECTED_CHIP_STYLE = { background: '#3BB273', borderColor: '#3BB273', color: '#FFFFFF', fontWeight: 600 }

export const QUICK_REASONS = ['Listed by mistake', 'Not ready for photography', 'Duplicate']

/**
 * Ask why a wrongly queued item is leaving the photography queue (Trac #392),
 * then remove it. Calls onRemoved when the server has done it.
 */
function RemoveFromQueueDialog({ item, onRemoved, onClose }) {
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const trimmed = reason.trim()
  const reasonOk = trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX

  async function handleRemove() {
    setSaving(true)
    setError(null)
    try {
      await api.post(`/api/warehouse/photography/${item.photography_queue_id}/cancel`, { reason: trimmed })
      onRemoved(item)
    } catch (err) {
      setError(err.message || 'Could not remove the item. Try again.')
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-from-queue-title"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480, width: '100%' }}
      >
        <div className="modal-header">
          <h3 id="remove-from-queue-title" style={{ margin: 0, fontSize: 18 }}>Remove from photography queue</h3>
          <button className="modal-close" onClick={onClose} disabled={saving} aria-label="Close">×</button>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 15 }}>
            <strong>{item.product_name}</strong> will leave the queue. Its photos are kept.
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} role="group" aria-label="Common reasons">
            {QUICK_REASONS.map(quick => (
              <button
                key={quick}
                type="button"
                className="btn btn-sm btn-secondary"
                aria-pressed={reason === quick}
                onClick={() => setReason(quick)}
                disabled={saving}
                style={reason === quick ? SELECTED_CHIP_STYLE : undefined}
              >
                {reason === quick && <span aria-hidden="true">{'✓ '}</span>}
                {quick}
              </button>
            ))}
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, fontWeight: 600 }}>
            Reason
            <textarea
              className="form-input"
              rows={3}
              maxLength={REASON_MAX}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={saving}
              style={{ fontSize: 16, fontWeight: 400 }}
            />
          </label>

          {error && <div role="alert" style={{ color: '#E5625E', fontSize: 14 }}>{error}</div>}
        </div>

        <div className="modal-footer" style={{ display: 'flex', gap: 12, padding: 16 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving} style={{ minHeight: 44, flex: 1 }}>
            Keep in queue
          </button>
          <button className="btn btn-danger" onClick={handleRemove} disabled={!reasonOk || saving} style={{ minHeight: 44, flex: 1 }}>
            {saving ? 'Removing...' : 'Remove from queue'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RemoveFromQueueDialog
